import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getItem, setItem } from '../../../../utils/localStorage';
import { ButtonLarge } from '../../../common/sharedComponents/buttons/ButtonLarge';
import { useTranslation } from 'react-i18next';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../helpers/showMessage';
import { ButtonBlockColumn } from './../../../common/sharedStyledComponents/GenericStyles.styled';
import { Headline3 } from '../../../common/typography';
import {
  ButtonContainer,
  HeaderContainer,
  StyledMyAuditContainer,
} from '../../../common/sharedStyledComponents/MyAuditGenericStyles.styled';
import { AppHeader } from '../../../common/sharedComponents/AppHeader';
import { Modal } from '../../../common/sharedComponents/Modal';
import { Nullable } from '../../../@types/genericTypes';
import { ISelfAssessmentCreation } from '../../../@types/selfAssessment';
import { DeleteTitleContainer } from '../../MyAudit/MyAuditList/styled.myAuditList';
import { SelfAssessmentPreviewCard } from '../../../selfAssessment/components/SelfAssessmentPreviewCard';
import moment from 'moment';
import { createSelfAssessmentv2 } from '../../../selfAssessment/requests';
import { sentryReport } from '../../../../helpers/sentry';

export interface IDepartmentPreview {
  name: string;
  subDepartments: string[];
}

export function SelfAssessmentPreview () {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const selfAssesment: ISelfAssessmentCreation[] = getItem('selfAssessments');
  const dueDate =
    selfAssesment[0].dueDate && new Date(selfAssesment[0].dueDate);

  const [selfAssessmentCreation, setSelfAssessmentCreation] =
    useState<ISelfAssessmentCreation[]>(selfAssesment);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [auditorIdToDelete, setAuditorIdToDelete] =
    useState<Nullable<number>>();
  const [isSending, setIsSending] = useState<boolean>(false);

  const handleSubmit = () => {
    setIsSending(true);
    const payload = parseSelfAssessmentPayload();
    createSelfAssessmentv2(payload)
      .then(() => {
        setItem('isFormData', false);
        showSuccessMessage(
          t('COMMON.SELF_ASSESSMENT.PAGES.PREVIEW.CREATE.SUCCESS')
        );
        setItem('isFormData', false);
        navigate('/myactions?filterBy=received');
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          summaryError: t('COMMON.SELF_ASSESSMENT.PAGES.PREVIEW.CREATE.FAIL'),
          path: '/myaudit',
        });
        showErrorMessage(t('COMMON.SELF_ASSESSMENT.PAGES.PREVIEW.CREATE.FAIL'));
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const handleEdit = (auditorId: Nullable<number>) => {
    const selfAssessmentCopy = [...selfAssessmentCreation];
    const filteredSelfAssessment = selfAssessmentCopy.filter(
      (selfAssessment) => selfAssessment.assignee.id !== auditorId
    );

    setItem('currentSelfAssessmentIndex', filteredSelfAssessment.length);
    setItem('selfAssessments', filteredSelfAssessment);
    navigate('/selfAssessment/selectDepartments');
  };

  const handleDelete = (auditorId: Nullable<number>) => {
    const selfAssessmentCopy = [...selfAssessmentCreation];
    const filteredSelfAssessment = selfAssessmentCopy.filter(
      (selfAssessment) => selfAssessment.assignee.id !== auditorId
    );

    setSelfAssessmentCreation(filteredSelfAssessment);
    if (selfAssessmentCopy.length === 0) {
      navigate('/selfAssessemt/selectDepartment');
    }
    setShowDeleteModal(false);
  };

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  //  TODO - Implement the parse when the API is available
  const parseSelfAssessmentPayload = () => {
    return selfAssessmentCreation.map((selfAssessment) => {
      return {
        brandId: selfAssessment.brandId,
        hotelId: selfAssessment.hotelId,
        subject: selfAssessment.subject,
        notes: selfAssessment.notes,
        assigneeId: selfAssessment.assignee.id,
        dueDate: selfAssessment.dueDate,
        subDepartments:
          selfAssessment.departments &&
          selfAssessment.departments.flatMap((department) =>
            department.subDepartments.map(
              (subDepartment) => subDepartment.subDepartmentId
            )
          ),
        templateId: selfAssessment.templateId,
      };
    });
  };

  const setDeleteFlow = (assigneeId: Nullable<number>) => {
    setShowDeleteModal(true);
    setAuditorIdToDelete(assigneeId);
  };

  return (
    <>
      <HeaderContainer>
        <AppHeader
          title={t('COMMON.SELF_ASSESSMENT.TITLE')}
          hasBackButton={false}
        />
      </HeaderContainer>
      <StyledMyAuditContainer>
        <Headline3>{t('COMMON.SELF_ASSESSMENT.PAGES.PREVIEW.TITLE')}</Headline3>
        {selfAssessmentCreation &&
          selfAssessmentCreation.length > 0 &&
          selfAssessmentCreation.map((selfAssessment) => {
            return (
              <SelfAssessmentPreviewCard
                key={selfAssessment.assignee.id}
                departments={
                  selfAssessment.departments && selfAssessment.departments
                }
                dueDate={moment(dueDate).format('DD/MM/YYYY')}
                assignee={selfAssessment.assignee}
                handleEdit={() => handleEdit(selfAssessment.assignee.id)}
                handleDelete={() => {
                  setDeleteFlow(selfAssessment.assignee.id);
                }}
              />
            );
          })}
        <ButtonContainer>
          <ButtonBlockColumn>
            <ButtonLarge
              title={t('COMMON.SELF_ASSESSMENT.BUTTON.SEND')}
              backgroundColor={'oxfordBlue'}
              height="h6"
              onClick={handleSubmit}
              disabled={(selfAssesment && !selfAssesment.length) || isSending}
            />
          </ButtonBlockColumn>
        </ButtonContainer>
      </StyledMyAuditContainer>
      {showDeleteModal && (
        <Modal isOpen={showDeleteModal} handleModalClose={toggleDeleteModal}>
          <ButtonBlockColumn>
            <DeleteTitleContainer>
              <Headline3>
                {t('COMMON.SELF_ASSESSMENT.PAGES.PREVIEW.DELETE_CONFIRMATION')}
              </Headline3>
            </DeleteTitleContainer>
            <ButtonLarge
              title={t('COMMON.BOTTOM_SHEET.DELETE')}
              onClick={() => handleDelete(auditorIdToDelete)}
              backgroundColor="rustyRed"
            />
            <ButtonLarge
              title={t('COMMON.BOTTOM_SHEET.CANCEL')}
              onClick={() => setShowDeleteModal(false)}
              outlined={true}
              inverted={true}
            />
          </ButtonBlockColumn>
        </Modal>
      )}
    </>
  );
}
